/**
 * BudgetFitter — Universal Social Share Engine
 * Handles popup dialogs, Web Share API, clipboard copy, and GA4 telemetry.
 */

(function () {
  'use strict';

  function initSocialShare() {
    // Reveal native share buttons if navigator.share is supported
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      document.querySelectorAll('.bfwt-share-native-btn').forEach(btn => {
        btn.classList.remove('tw-hidden');
      });
    }

    function openModal(modal, trigger) {
      if (!modal) return;
      if (modal.parentElement !== document.body) {
        document.body.appendChild(modal);
      }
      document.body.classList.add('bfwt-popup-is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      setTimeout(() => {
        modal.classList.add('show');
        if (window.BFWT_Accessibility) {
          window.BFWT_Accessibility.initModalAccessibility(modal, trigger);
        }
      }, 10);
    }

    function closeModal(modal) {
      if (!modal || !modal.classList.contains('show')) return;
      modal.classList.remove('show');
      if (window.BFWT_Accessibility) {
        window.BFWT_Accessibility.removeModalAccessibility();
      }
      setTimeout(() => {
        if (document.querySelectorAll('.bfwt-popup-overlay.show').length === 0) {
          document.body.classList.remove('bfwt-popup-is-open');
        }
      }, 300);
      document.querySelectorAll('.bfwt-social-share-trigger').forEach(t =>
        t.setAttribute('aria-expanded', 'false')
      );
    }

    function closeAllModals() {
      document.querySelectorAll('.bfwt-social-share-modal.show').forEach(closeModal);
      document.querySelectorAll('.bfwt-social-share-trigger').forEach(t =>
        t.setAttribute('aria-expanded', 'false')
      );
    }

    // 1. TRIGGER & MODAL TOGGLE HANDLERS
    document.addEventListener('click', function (e) {
      // Close button inside modal
      if (e.target.closest('.bfwt-popup-close, .bfwt-social-share-close')) {
        e.preventDefault();
        const modal = e.target.closest('.bfwt-social-share-modal');
        closeModal(modal);
        return;
      }

      // Backdrop click (click outside .bfwt-popup-box)
      if (e.target.classList.contains('bfwt-social-share-modal')) {
        closeModal(e.target);
        return;
      }

      // Trigger button click
      const trigger = e.target.closest('.bfwt-social-share-trigger');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();

        const targetId = trigger.getAttribute('data-modal-target');
        let modal = (targetId && document.getElementById(targetId)) || trigger._shareModal;
        if (!modal) {
          const dropdown = trigger.closest('.bfwt-social-share-dropdown');
          modal = (dropdown && dropdown.querySelector('.bfwt-social-share-modal')) || document.querySelector('.bfwt-social-share-modal');
        }

        if (modal) {
          trigger._shareModal = modal;
          const isOpen = modal.classList.contains('show');
          if (isOpen) {
            closeModal(modal);
          } else {
            closeAllModals();
            openModal(modal, trigger);
          }
          return;
        }
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeAllModals();
      }
    });

    // 2. SOCIAL SHARE CHANNELS (Universal Links on Mobile / Popup on Desktop)
    document.addEventListener('click', function (e) {
      const link = e.target.closest('.bfwt-share-link');
      if (!link) return;

      const channel = link.getAttribute('data-channel');
      const href = link.getAttribute('href');

      if (!href) return;

      trackShare(channel || 'channel', href);

      if (href.startsWith('mailto:')) {
        e.preventDefault();
        window.location.href = href;
        return;
      }

      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        // Allow native link navigation on mobile (target="_blank") so OS Universal Links
        // open the installed native app (WhatsApp, Telegram, X, Reddit) with prefilled text!
        return;
      }

      // Desktop: Open centered popup window
      e.preventDefault();
      const width = 600;
      const height = 520;
      const left = window.innerWidth / 2 - width / 2 + window.screenX;
      const top = window.innerHeight / 2 - height / 2 + window.screenY;

      window.open(
        href,
        'bfwt_share_' + (channel || 'window'),
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, copyhistory=no, width=${width}, height=${height}, top=${top}, left=${left}`
      );
    });

    // 3. NATIVE SHARE (More Options / AirDrop / Other Apps)
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.bfwt-share-native-btn');
      if (!btn) return;

      e.preventDefault();
      const shareUrl = btn.getAttribute('data-share-url') || window.location.href;
      const shareTitle = btn.getAttribute('data-share-title') || document.title;
      const shareText = btn.getAttribute('data-share-text') || shareTitle;

      // Close open modals and menus
      closeAllModals();

      if (typeof navigator !== 'undefined' && navigator.share) {
        navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        }).then(() => {
          trackShare('native_more', shareUrl);
        }).catch((err) => {
          if (err && err.name !== 'AbortError') {
            console.warn('Native share error:', err);
          }
        });
      }
    });

    // 4. COPY LINK TO CLIPBOARD
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.bfwt-share-copy-btn');
      if (!btn) return;

      e.preventDefault();
      const shareUrl = btn.getAttribute('data-share-url') || window.location.href;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareUrl).then(() => {
          showCopySuccess(btn);
          trackShare('copy_link', shareUrl);
        }).catch(() => {
          fallbackCopy(shareUrl, btn);
        });
      } else {
        fallbackCopy(shareUrl, btn);
      }
    });
  }

  function fallbackCopy(text, btn) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      showCopySuccess(btn);
      trackShare('copy_link', text);
    } catch (err) {}
    document.body.removeChild(textArea);
  }

  function showCopySuccess(btn) {
    const label = btn.querySelector('.bfwt-copy-label');
    const icon = btn.querySelector('.bf-icon, .material-symbols-rounded');
    const origText = label ? label.textContent : '';
    const origIcon = icon ? (icon.getAttribute('data-bf-icon') || icon.textContent || 'link') : 'link';

    if (label) label.textContent = 'Copied!';
    if (icon) icon.textContent = 'check';
    btn.classList.add('tw-text-accent', 'tw-border-accent');

    setTimeout(() => {
      if (label) label.textContent = origText;
      if (icon) icon.textContent = origIcon;
      btn.classList.remove('tw-text-accent', 'tw-border-accent');
    }, 2000);
  }

  function trackShare(method, itemUrl) {
    if (typeof window.bfTrack === 'function') {
      window.bfTrack('social_share', {
        event_category: 'engagement',
        method: method,
        content_type: 'cpt_single',
        item_id: itemUrl,
      });
    } else if (typeof window.gtag === 'function') {
      window.gtag('event', 'share', {
        method: method,
        content_type: 'cpt_single',
        item_id: itemUrl
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSocialShare);
  } else {
    initSocialShare();
  }
})();
